import { useEffect, useState } from "react";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import { UserAnalyticsService } from "@analytics/index";

import { usePopupActions, usePopupState } from "@store/popupStore";

import theme from "@theme/index";

import { extractTextFromJsx } from "@utilities/extractStringFromJsx";

/**
 * This component renders a dialog box with a title, content, and action buttons for confirming or rejecting an action.
 */
export default function Confirmation() {
  const { confirmation } = usePopupState();
  const PopupActions = usePopupActions();
  const [confirming, setConfirming] = useState(false);

  const { content, customOnConfirmText, customOnRejectText, isDanger, isOpen, onConfirm, onExit, onReject, title } =
    confirmation;

  /**
   * Handles the confirmation action.
   * Closes the confirmation dialog, triggers the `onConfirm` callback and triggers a tracking function.
   */
  const handleOk = async () => {
    if (onConfirm) {
      setConfirming(true);
      onConfirm();
    }
    PopupActions.closeConfirmation();
    setConfirming(false);
  };

  /**
   * Handles the rejection action.
   * Closes the confirmation dialog, triggers the `onReject` callback and triggers a tracking functions.
   */
  const handleReject = () => {
    PopupActions.closeConfirmation();
    if (onReject) {
      onReject();
    }
  };

  /**
   * Handles the exit action.
   * Closes the confirmation dialog, triggers the `onExit` callback and triggers a tracking function.
   */
  const handleExit = () => {
    PopupActions.closeConfirmation();
    if (onExit) {
      onExit();
    }
  };

  return isOpen ? (
    <Dialog
      aria-labelledby="dialog-title"
      onClose={handleExit}
      open={isOpen}
      PaperProps={{
        sx: {
          padding: theme.spacing(1),
        },
      }}
    >
      {title && (
        <DialogTitle id="dialog-title" sx={{ borderBottom: `1px solid ${theme.palette.borderColor.dark}`, mb: 2 }}>
          {title}
        </DialogTitle>
      )}

      <DialogContent>{content}</DialogContent>

      <DialogActions>
        <Button color={isDanger ? "error" : "primary"} onClick={handleOk} variant="contained">
          {confirming ? <CircularProgress size={24} /> : customOnConfirmText ?? "Ok"}
        </Button>
        <Button color="secondary" onClick={handleReject}>
          {customOnRejectText ?? "Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <div />
  );
}
